// QueuedPhotosBadge — small fixed pill shown to talent whenever photos
// are waiting in the offline upload queue (venue dead zones).
import React, { useEffect, useState, useCallback } from 'react';
import { CloudUpload, WifiOff } from 'lucide-react';
import { getQueuedCount, flushQueue } from '../utils/offlineUploadQueue';

export const QueuedPhotosBadge = () => {
  const [count, setCount] = useState(0);
  const [online, setOnline] = useState(navigator.onLine);

  const refresh = useCallback(async () => {
    setCount(await getQueuedCount());
    setOnline(navigator.onLine);
  }, []);

  useEffect(() => {
    refresh();
    const onChange = () => refresh();
    window.addEventListener('offline-queue-changed', onChange);
    window.addEventListener('online', onChange);
    window.addEventListener('offline', onChange);
    const iv = setInterval(refresh, 20000);
    return () => {
      window.removeEventListener('offline-queue-changed', onChange);
      window.removeEventListener('online', onChange);
      window.removeEventListener('offline', onChange);
      clearInterval(iv);
    };
  }, [refresh]);

  if (count === 0) return null;

  return (
    <button
      type="button"
      onClick={() => flushQueue()}
      data-testid="queued-photos-badge"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-2
                 rounded-full px-4 py-2 text-xs font-semibold text-white shadow-lg
                 transition-colors bg-slate-900/90 backdrop-blur border border-white/10
                 hover:bg-slate-800"
      title={online ? 'Tap to retry now' : 'Will upload automatically when you are back online'}
    >
      {online
        ? <CloudUpload className="w-4 h-4 text-cyan-400 animate-pulse" />
        : <WifiOff className="w-4 h-4 text-amber-400" />}
      {count} photo{count > 1 ? 's' : ''} waiting to upload
      {!online && <span className="text-amber-300 font-normal">· offline</span>}
    </button>
  );
};
